<script module lang="ts">
	export enum ButtonType {
		BRAND,
		NEUTRAL,
		SECONDARY_OUTLINE,
		DESTRUCTIVE,
		DESTRUCTIVE_OUTLINE,
		WARNING,
		ERROR,
		SUCCESS,
		INVERSE,
		INVERSE_OUTLINE,
		TERTIARY,
		TERTIARY_INVERSE,
		SOFT_OUTLINE,
		TRANSPARENT,
		CUSTOM,
		LINK
	}
	export enum ButtonSize {
		XX_SMALL,
		EXTRA_SMALL,
		SMALL,
		MEDIUM,
		LARGE,
		EXTRA_LARGE,
		X_LARGE
	}
</script>

<script lang="ts">
	import { type ComponentProps } from 'svelte'
	import { Badge, Icon, LoadingSpinner, iconSizeMap } from './index.js'

	interface Props {
		id?: string
		testid?: string
		type?: string
		isLoading?: boolean
		buttonText?: string
		buttonType?: ButtonType
		buttonSize?: ButtonSize
		disabled?: boolean
		readonly?: boolean
		extraProps?: string
		textExtraProps?: string
		iconProps?: { name?: string } & Record<string, string | number | boolean>
		iconPosition?: 'left' | 'right'
		thin?: boolean
		rounded?: boolean
		fillWidth?: boolean
		badgeValue?: number
		showCornerTriangle?: boolean
		cornerTriangleProps?: Partial<ComponentProps<typeof Icon>>
		onclick?: (() => void) | null
		children?: import('svelte').Snippet | null
		loaderColor?: string
		badgeProps?: ComponentProps<typeof Badge>
	}

	let {
		id,
		testid,
		type = 'button' as const,
		isLoading = false,
		buttonText = '',
		buttonType = ButtonType.BRAND,
		buttonSize = ButtonSize.SMALL,
		disabled = false,
		readonly = false,
		extraProps = '',
		textExtraProps = '',
		iconProps,
		iconPosition = 'left',
		thin = false,
		rounded = false,
		fillWidth = false,
		badgeValue = 0,
		showCornerTriangle = false,
		cornerTriangleProps = {},
		onclick = null,
		children = null,
		loaderColor,
		badgeProps
	}: Props = $props()

	function onClick(event: Event): void {
		if (isLoading || disabled || readonly) {
			return
		}
		event.stopPropagation()
		onclick?.()
	}

	const hasIcon = $derived(!!iconProps?.name)

	const isIconOnly = $derived.by(() => {
		return !buttonText && hasIcon
	})

	const resolvedLoaderSize = $derived.by(() => {
		const iconSize = (iconProps as Record<string, unknown>)?.size as string | undefined
		if (iconSize && iconSize in iconSizeMap) {
			return parseInt(iconSizeMap[iconSize as keyof typeof iconSizeMap])
		}
		return 24
	})
</script>

<button
	{type}
	{id}
	data-testid={testid}
	onclick={onClick}
	{disabled}
	class="button-class"
	class:brand={buttonType === ButtonType.BRAND}
	class:neutral={buttonType === ButtonType.NEUTRAL}
	class:destructive={buttonType === ButtonType.DESTRUCTIVE}
	class:destructive-outline={buttonType === ButtonType.DESTRUCTIVE_OUTLINE}
	class:warning={buttonType === ButtonType.WARNING}
	class:error={buttonType === ButtonType.ERROR}
	class:success={buttonType === ButtonType.SUCCESS}
	class:inverse={buttonType === ButtonType.INVERSE}
	class:inverse-outline={buttonType === ButtonType.INVERSE_OUTLINE}
	class:tertiary={buttonType === ButtonType.TERTIARY}
	class:tertiary-inverse={buttonType === ButtonType.TERTIARY_INVERSE}
	class:secondary-outline={buttonType === ButtonType.SECONDARY_OUTLINE}
	class:soft-outline={buttonType === ButtonType.SOFT_OUTLINE}
	class:transparent={buttonType === ButtonType.TRANSPARENT}
	class:custom={buttonType === ButtonType.CUSTOM}
	class:link={buttonType === ButtonType.LINK}
	class:xx-small={buttonSize === ButtonSize.XX_SMALL}
	class:extra-small={buttonSize === ButtonSize.EXTRA_SMALL}
	class:small={buttonSize === ButtonSize.SMALL}
	class:medium={buttonSize === ButtonSize.MEDIUM}
	class:large={buttonSize === ButtonSize.LARGE}
	class:extra-large={buttonSize === ButtonSize.EXTRA_LARGE}
	class:x-large={buttonSize === ButtonSize.X_LARGE}
	class:fill-width={fillWidth}
	class:icon-only={isIconOnly}
	class:thin
	class:rounded
	class:disabled
	class:readonly
	class:loading={isLoading}
	class:w-full={fillWidth}
	style={extraProps}
>
	<div class="inner-container" class:icon-only={isIconOnly}>
		{#if hasIcon && iconPosition === 'left' && !(isIconOnly && isLoading)}
			<Icon {...iconProps} />
		{/if}
		{#if isLoading}
			<LoadingSpinner color={loaderColor} size={resolvedLoaderSize} />
			<span class="hidden-text button-text" style={textExtraProps}>{buttonText}</span>
		{:else}
			{@render children?.()}
			<span class="button-text" style={textExtraProps}>{buttonText}</span>
		{/if}
		{#if hasIcon && iconPosition === 'right' && !(isIconOnly && isLoading)}
			<Icon {...iconProps} />
		{/if}
		{#if badgeProps}
			<div class="ml-2">
				<Badge {...badgeProps} />
			</div>
		{:else if badgeValue}
			<div class="badge ml-2">{badgeValue}</div>
		{/if}
	</div>
	{#if showCornerTriangle}
		<div class="corner-triangle-container">
			<Icon name="cornerTriangle" {...cornerTriangleProps} />
		</div>
	{/if}
</button>

<style lang="postcss">
	.button-class {
		@apply flex items-center justify-center transition-all duration-200 font-semibold outline-none border-0 relative;
		@apply hover:bg-opacity-95 focus-visible:ring-2 focus-visible:ring-[#007acc] focus-visible:ring-offset-2;
		@apply disabled:cursor-not-allowed;
	}

	.button-class.rounded {
		@apply rounded-full;
	}

	.inner-container {
		@apply flex items-center gap-[0.25rem] relative;
	}

	.inner-container.icon-only {
		@apply !gap-0;
	}

	.icon {
		@apply inline-block w-4 h-4 align-middle;
	}

	.button-text {
		@apply whitespace-nowrap;
	}

	.hidden-text {
		@apply invisible;
	}

	.brand {
		@apply bg-[#007acc] text-white hover:bg-[#007acc] active:bg-[#0062a3];
	}

	.tertiary {
		@apply text-brand underline underline-offset-2 hover:text-brand disabled:bg-[#003a66] disabled:text-[#003a66] hover:bg-[#0049800D] active:bg-[#0049801A];
	}

	.tertiary.medium {
		@apply px-5;
	}

	.tertiary.small {
		@apply px-4 py-1.5;
	}

	.tertiary.extra-small,
	.tertiary.extra-large {
		@apply px-0 py-0;
	}

	.tertiary:disabled {
		@apply bg-transparent text-gray-400;
	}

	.tertiary-inverse {
		@apply text-[#001D33B2] px-5 underline underline-offset-2 hover:bg-[#0049800D] active:bg-[#0049801A] disabled:bg-[#003a66] disabled:text-[#003a66];
	}

	.tertiary-inverse:disabled {
		@apply bg-transparent text-gray-400;
	}

	.link {
		@apply text-brand underline underline-offset-2 font-normal
		hover:text-brand hover:text-opacity-80
		p-0 h-auto min-h-0;
	}

	.link:disabled {
		@apply bg-transparent text-gray-400;
	}

	.neutral {
		@apply bg-transparent bg-opacity-90 border border-[#007acc] text-[#007ACC] active:bg-[#007acc1a];
	}

	.destructive {
		@apply bg-[#e4485b] text-white active:bg-[#c73349];
	}

	.inverse {
		@apply bg-white text-[#001D33B2] text-opacity-90 active:bg-[#E4E7EC];
	}

	.inverse-outline {
		@apply bg-white border border-[1px] border-[#002C4D80] text-[#001D33B2] text-opacity-90 active:bg-[#E4E7EC];
	}

	.destructive-outline {
		@apply bg-transparent border border-[#E4485B] text-[#E4485B]
		hover:bg-[#E4485B0D] active:bg-[#E4485B1A]
		disabled:border-[#E4485B33] disabled:text-[#E4485B33];
	}

	.warning {
		@apply bg-transparent border border-[#CB9A0B] text-[#CB9A0B]
		hover:bg-[#CB9A0B0D] active:bg-[#CB9A0B1A]
		disabled:border-[#CB9A0B33] disabled:text-[#CB9A0B33];
	}

	.error {
		@apply bg-transparent border border-[#E4485B] text-[#E4485B]
		hover:bg-[#E4485B0D] active:bg-[#E4485B1A]
		disabled:border-[#E4485B33] disabled:text-[#E4485B33];
	}

	.success {
		@apply bg-transparent border border-[#4CAF50] text-[#4CAF50]
		hover:bg-[#4CAF500D] active:bg-[#4CAF501A]
		disabled:border-[#4CAF5033] disabled:text-[#4CAF5033];
	}

	.secondary-outline {
		@apply bg-transparent border border-[#002C4D80] text-[#475467]
		hover:bg-[#F2F4F7] active:bg-[#E4E7EC]
		disabled:border-[#E4E7EC] disabled:text-[#98A2B3];
	}

	.soft-outline {
		@apply bg-transparent border border-[#A3DAFF] text-[#A3DAFF]
		hover:bg-[#A3DAFF1A] active:bg-[#A3DAFF33]
		disabled:border-[#A3DAFF33] disabled:text-[#007ACC33];
	}

	.transparent {
		@apply bg-transparent text-[#001D33B2] border border-transparent
		hover:bg-[#0049800D] active:bg-[#0049801A]
		disabled:text-[#98A2B3];
	}

	.transparent:disabled {
		@apply bg-transparent;
	}

	.xx-small {
		@apply h-5 px-1.5 py-0.5 rounded-md font-normal;
		font-size: 10px;
	}

	.extra-small {
		@apply h-6 px-2 py-1 rounded-lg text-xs font-normal;
	}

	.small {
		@apply h-8 px-3 py-1 rounded-lg text-sm;
	}

	.medium {
		@apply h-10 px-4 py-2 rounded-lg text-base;
	}

	.large {
		@apply h-12 px-5 py-2.5 rounded-lg text-base font-semibold;
	}

	.extra-large {
		@apply h-14 px-6 py-3 rounded-xl text-xl;
	}

	.x-large {
		@apply h-16 px-8 py-4 rounded-2xl text-2xl;
	}

	.button-class.icon-only.extra-small {
		@apply w-6 h-6;
	}

	.button-class.icon-only.small {
		@apply px-1 py-1 w-8 h-8;
	}

	.button-class.icon-only.medium {
		@apply px-2 py-2;
	}

	.button-class.icon-only.large {
		@apply px-2.5 py-2.5 w-12 h-12;
	}

	.button-class.custom {
		@apply m-[unset] h-[unset] w-[unset] min-w-[unset] p-[unset] outline-none outline-transparent outline-offset-2 focus:outline-none focus:ring-0 focus:ring-offset-0 active:ring-0 active:ring-offset-0;
	}

	:global(.inner-container .spinner) {
		position: absolute;
	}

	:global(.inner-container.icon-only .spinner) {
		position: static;
	}

	:global(.tertiary-inverse .inner-container .spinner circle) {
		stroke: #002c4d80;
	}

	:global(.button .icon-wrapper) {
		@apply !flex;
	}

	.corner-triangle-container {
		@apply flex absolute bottom-0 right-0;
	}

	.badge {
		@apply flex h-6 min-w-6 px-2 justify-center items-center gap-1 rounded-full
		bg-[rgba(0,73,128,0.06)] text-[rgba(0,29,51,0.70)] text-center
		text-sm font-medium leading-5;
	}

	.thin {
		@apply font-normal;
	}

	.disabled {
		opacity: 0.3;
		pointer-events: none;
	}

	.readonly {
		@apply cursor-default;
	}

	.button-class.link {
		@apply !p-0 !h-auto !min-h-0 !rounded-none;
	}
</style>
